import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../../../contexts/ModalContext/ModalContext'
import { useBilling } from '../../../hooks/useBilling'
import {
  BillingContainer,
  Header,
  Title,
  BackButton,
  LoadingContainer,
  ErrorContainer,
  CardsGrid,
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  PlanPrice,
  PriceAmount,
  PricePeriod,
  StatusBadge,
  UsageSection,
  UsageItem,
  UsageLabel,
  UsageValue,
  ProgressBar,
  ProgressFill,
  RenewalInfo,
  OverageSection,
  OverageTitle,
  OverageItem,
  ActionButtons,
  Button,
  PlanComparisonSection,
  SectionTitle,
  PlansGrid,
  PlanCard,
  PlanName,
  PlanDescription,
  FeaturesList,
  Feature,
} from './styled'

const BillingDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { showError } = useModal()
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const {
    billingData,
    loading,
    error,
    refetch,
    createCheckoutSession,
    createPortalSession,
    getCurrentPlanDetails,
    getUsagePercentage,
    isOverLimit,
    getDaysUntilRenewal,
    getOverageCharges,
  } = useBilling()

  const handleUpgrade = async (plan: 'standard' | 'premium') => {
    try {
      setActionLoading(`upgrade-${plan}`)
      await createCheckoutSession(plan)
    } catch (err) {
      console.error('Error upgrading plan:', err)
      showError('Failed to start upgrade process. Please try again.')
    } finally {
      setActionLoading(null)
    }
  }

  const handleManageSubscription = async () => {
    try {
      setActionLoading('manage')
      await createPortalSession()
    } catch (err) {
      console.error('Error opening billing portal:', err)
      showError('Failed to open billing portal. Please try again.')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <BillingContainer>
        <LoadingContainer>Loading your billing information...</LoadingContainer>
      </BillingContainer>
    )
  }

  if (error) {
    return (
      <BillingContainer>
        <ErrorContainer>
          <h3>Unable to load billing information</h3>
          <p>{error}</p>
          <Button onClick={refetch}>Try Again</Button>
        </ErrorContainer>
      </BillingContainer>
    )
  }

  if (!billingData) {
    return (
      <BillingContainer>
        <ErrorContainer>
          <h3>No billing information available</h3>
          <Button onClick={refetch}>Refresh</Button>
        </ErrorContainer>
      </BillingContainer>
    )
  }

  const currentPlan = getCurrentPlanDetails()
  const subscription = billingData.subscription
  const daysUntilRenewal = getDaysUntilRenewal()
  const overageCharges = getOverageCharges()
  const hasOverages =
    overageCharges.mapOverage > 0 || overageCharges.viewOverage > 0

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`

  const mapUsagePercentage = getUsagePercentage(
    billingData.totalMaps,
    currentPlan.limits.maxMaps + (subscription?.additionalMaps || 0),
  )

  const viewUsagePercentage = getUsagePercentage(
    billingData.monthlyUsage,
    currentPlan.limits.includedViews,
  )

  const isMapOverLimit = isOverLimit(
    billingData.totalMaps,
    currentPlan.limits.maxMaps + (subscription?.additionalMaps || 0),
  )

  const isViewOverLimit = isOverLimit(
    billingData.monthlyUsage,
    currentPlan.limits.includedViews,
  )

  return (
    <BillingContainer>
      <Header>
        <div>
          <BackButton onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </BackButton>
        </div>
        <Title>Billing & Usage</Title>
        <div></div>
      </Header>

      <CardsGrid>
        {/* Current Plan Card */}
        <Card $featured>
          <CardHeader>
            <CardTitle>{currentPlan.name}</CardTitle>
            <CardSubtitle>
              {subscription ? (
                <>
                  <StatusBadge $status={subscription.status}>
                    {subscription.status}
                  </StatusBadge>
                  {subscription.cancelAtPeriodEnd && (
                    <span style={{ color: '#dc3545', marginLeft: '0.5rem' }}>
                      (Canceling)
                    </span>
                  )}
                </>
              ) : (
                <StatusBadge $status="active">Active</StatusBadge>
              )}
            </CardSubtitle>
          </CardHeader>

          <PlanPrice>
            <PriceAmount>{formatCurrency(currentPlan.price)}</PriceAmount>
            <PricePeriod>/month</PricePeriod>
          </PlanPrice>

          {subscription && daysUntilRenewal && (
            <RenewalInfo>
              {subscription.cancelAtPeriodEnd
                ? `Plan expires in ${daysUntilRenewal} days`
                : `Next billing: ${daysUntilRenewal} days`}
            </RenewalInfo>
          )}

          <ActionButtons>
            {subscription && subscription.plan !== 'free' && (
              <Button
                $variant="secondary"
                $fullWidth
                onClick={handleManageSubscription}
                disabled={actionLoading === 'manage'}>
                {actionLoading === 'manage'
                  ? 'Loading...'
                  : 'Manage Subscription'}
              </Button>
            )}

            {(!subscription ||
              subscription.plan === 'free' ||
              subscription.status === 'past_due' ||
              subscription.status === 'incomplete') && (
              <>
                <Button
                  $variant="primary"
                  $fullWidth
                  onClick={() => handleUpgrade('standard')}
                  disabled={actionLoading?.includes('upgrade')}>
                  {actionLoading === 'upgrade-standard'
                    ? 'Loading...'
                    : 'Upgrade to Standard'}
                </Button>
                <Button
                  $variant="success"
                  $fullWidth
                  onClick={() => handleUpgrade('premium')}
                  disabled={actionLoading?.includes('upgrade')}>
                  {actionLoading === 'upgrade-premium'
                    ? 'Loading...'
                    : 'Upgrade to Premium'}
                </Button>
              </>
            )}

            {subscription &&
              subscription.plan === 'standard' &&
              subscription.status === 'active' && (
                <Button
                  $variant="success"
                  $fullWidth
                  onClick={() => handleUpgrade('premium')}
                  disabled={actionLoading?.includes('upgrade')}>
                  {actionLoading === 'upgrade-premium'
                    ? 'Loading...'
                    : 'Upgrade to Premium'}
                </Button>
              )}
          </ActionButtons>
        </Card>

        {/* Usage Card */}
        <Card>
          <CardHeader>
            <CardTitle>Current Usage</CardTitle>
            <CardSubtitle>This month's activity</CardSubtitle>
          </CardHeader>

          <UsageSection>
            <UsageItem>
              <UsageLabel>
                <span>Total Floorplans</span>
                <UsageValue $overLimit={isMapOverLimit}>
                  {billingData.totalMaps || 0} /{' '}
                  {currentPlan.limits.maxMaps === -1
                    ? '∞'
                    : currentPlan.limits.maxMaps +
                      (subscription?.additionalMaps || 0)}
                </UsageValue>
              </UsageLabel>
              <ProgressBar>
                <ProgressFill
                  $percentage={mapUsagePercentage}
                  $overLimit={isMapOverLimit}
                />
              </ProgressBar>
            </UsageItem>

            <UsageItem>
              <UsageLabel>
                <span>Views</span>
                <UsageValue $overLimit={isViewOverLimit}>
                  {billingData.monthlyUsage.toLocaleString()} /{' '}
                  {currentPlan.limits.includedViews === -1
                    ? '∞'
                    : currentPlan.limits.includedViews.toLocaleString()}
                </UsageValue>
              </UsageLabel>
              <ProgressBar>
                <ProgressFill
                  $percentage={viewUsagePercentage}
                  $overLimit={isViewOverLimit}
                />
              </ProgressBar>
            </UsageItem>
          </UsageSection>

          {hasOverages && (
            <OverageSection>
              <OverageTitle>Overage Charges This Month</OverageTitle>
              {overageCharges.mapOverage > 0 && (
                <OverageItem>
                  <span>{overageCharges.mapOverage} extra floorplans</span>
                  <span>{formatCurrency(overageCharges.mapOverageCost)}</span>
                </OverageItem>
              )}
              {overageCharges.viewOverage > 0 && (
                <OverageItem>
                  <span>{overageCharges.viewOverage} × 100 extra views</span>
                  <span>{formatCurrency(overageCharges.viewOverageCost)}</span>
                </OverageItem>
              )}
              <OverageItem>
                <span>Total Overage</span>
                <span>
                  {formatCurrency(
                    overageCharges.mapOverageCost +
                      overageCharges.viewOverageCost,
                  )}
                </span>
              </OverageItem>
            </OverageSection>
          )}

          <ActionButtons>
            <Button $variant="secondary" $fullWidth onClick={refetch}>
              Refresh Usage
            </Button>
          </ActionButtons>
        </Card>
      </CardsGrid>

      {/* Plan Comparison Section */}
      {(!subscription ||
        subscription.plan === 'free' ||
        subscription.plan === 'standard') && (
        <PlanComparisonSection>
          <SectionTitle>
            {!subscription || subscription.plan === 'free'
              ? 'Choose Your Plan'
              : 'Upgrade Your Plan'}
          </SectionTitle>
          <PlansGrid>
            <PlanCard $current={!subscription || subscription.plan === 'free'}>
              <PlanName>Free Plan</PlanName>
              <PlanPrice>
                <PriceAmount>$0</PriceAmount>
                <PricePeriod>/month</PricePeriod>
              </PlanPrice>
              <PlanDescription>Perfect for trying out</PlanDescription>
              <FeaturesList>
                <Feature>1 floorplan</Feature>
                <Feature>100 views/month</Feature>
                <Feature>Basic sharing</Feature>
              </FeaturesList>
            </PlanCard>

            <PlanCard
              $current={subscription?.plan === 'standard'}
              $recommended={!subscription || subscription.plan === 'free'}>
              <PlanName>Standard Plan</PlanName>
              <PlanPrice>
                <PriceAmount>$20</PriceAmount>
                <PricePeriod>/month</PricePeriod>
              </PlanPrice>
              <PlanDescription>Great for small teams</PlanDescription>
              <FeaturesList>
                <Feature>50 floorplans</Feature>
                <Feature>10,000 views/month</Feature>
                <Feature>Advanced sharing</Feature>
                <Feature>Custom branding</Feature>
                <Feature>Export capabilities</Feature>
                <Feature>$1 per extra floorplan</Feature>
              </FeaturesList>
              {subscription?.plan !== 'standard' && (
                <Button
                  $variant="primary"
                  $fullWidth
                  onClick={() => handleUpgrade('standard')}
                  disabled={actionLoading?.includes('upgrade')}>
                  {actionLoading === 'upgrade-standard'
                    ? 'Loading...'
                    : 'Choose Standard'}
                </Button>
              )}
            </PlanCard>

            <PlanCard
              $current={subscription?.plan === 'premium'}
              $recommended={subscription?.plan === 'standard'}>
              <PlanName>Premium Plan</PlanName>
              <PlanPrice>
                <PriceAmount>$50</PriceAmount>
                <PricePeriod>/month</PricePeriod>
              </PlanPrice>
              <PlanDescription>Perfect for growing teams</PlanDescription>
              <FeaturesList>
                <Feature>75 floorplans</Feature>
                <Feature>50,000 views/month</Feature>
                <Feature>All Standard features</Feature>
                <Feature>Priority support</Feature>
                <Feature>Advanced analytics</Feature>
                <Feature>$1 per extra floorplan</Feature>
              </FeaturesList>
              {subscription?.plan !== 'premium' && (
                <Button
                  $variant="success"
                  $fullWidth
                  onClick={() => handleUpgrade('premium')}
                  disabled={actionLoading?.includes('upgrade')}>
                  {actionLoading === 'upgrade-premium'
                    ? 'Loading...'
                    : 'Choose Premium'}
                </Button>
              )}
            </PlanCard>
          </PlansGrid>
        </PlanComparisonSection>
      )}
    </BillingContainer>
  )
}

export { BillingDashboard }
